import { useState, useEffect } from 'react';

interface SoftLaunchState {
  isEnabled: boolean;
  trialDays: number;
  standardTrialDays: number;
  messaging: {
    badge: string;
    title: string;
    subtitle: string;
    description: string;
  };
}

const buildMessaging = (trialDays: number) => ({
  badge: 'Early Access',
  title: 'Early Access Program',
  subtitle: `${trialDays} days free • Full features`,
  description:
    "You're part of our Early Access program! Enjoy full access while we finalize the platform. Everything is secure and production-ready — we're just making sure every detail is perfect before general availability.",
});

export const useSoftLaunch = (): SoftLaunchState => {
  // Initial state uses build-time defaults so initial render isn't wrong
  // before the runtime fetch completes
  const initialEnabled = (() => {
    try {
      return process.env.REACT_APP_SOFT_LAUNCH_MODE === 'true';
    } catch {
      return false;
    }
  })();

  const initialTrialDays = (() => {
    try {
      return parseInt(process.env.REACT_APP_SOFT_LAUNCH_DAYS || '90', 10);
    } catch {
      return 90;
    }
  })();

  const [isEnabled, setIsEnabled] = useState(initialEnabled);
  const [trialDays, setTrialDays] = useState(initialTrialDays);
  const [standardTrialDays, setStandardTrialDays] = useState(14);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/branding')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (typeof data?.soft_launch_mode === 'boolean') {
          setIsEnabled(data.soft_launch_mode);
        }
        if (typeof data?.soft_launch_trial_days === 'number') {
          setTrialDays(data.soft_launch_trial_days);
        }
        if (typeof data?.trial_days === 'number') {
          setStandardTrialDays(data.trial_days);
        }
      })
      .catch(() => {
        // Keep build-time fallbacks on error
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    isEnabled,
    trialDays,
    standardTrialDays,
    messaging: buildMessaging(trialDays),
  };
};

export default useSoftLaunch;
